import React, { useRef } from 'react'
import { useField } from 'formik'
import BsForm from 'react-bootstrap/Form'
import { Typeahead, Token, Highlighter } from 'react-bootstrap-typeahead'
import PropTypes from 'prop-types'
import { Row, Col } from 'react-bootstrap'
import { sortBy } from 'lodash'
import TopicIcon from '~/components/shared/TopicIcon'
import withLocale from '~/hocs/locale'

const TopicsInput = ({
  label, row, options, ...props
}) => {
  const [field, meta, helpers] = useField(props)

  const sortedOptions = sortBy(options, 'name')
  const selectedIds = (Array.isArray(field.value) && field.value?.map((f) => f.id)) || []
  const selected = sortedOptions.filter((option) => selectedIds.includes(option.id))

  const ref = useRef()

  const handleChange = (selectedOptions) => {
    helpers.setValue(selectedOptions)
  }

  const handleBlur = () => {
    helpers.setTouched(true)
  }

  const handleFocus = (e) => {
    setTimeout(() => e.target.scrollIntoView({
      behavior: 'auto',
      block: 'center',
      inline: 'center'
    }), 300)
  }

  const inputTypeahead = (
    <Typeahead
      {...field}
      {...props}
      inputProps={{ ref }}
      id={field.name}
      onChange={handleChange}
      onBlur={handleBlur}
      isInvalid={meta.touched && meta.error}
      isValid={meta.touched && !meta.error}
      options={sortedOptions}
      disabled={!options}
      selected={selected}
      multiple
      labelKey="name"
      onFocus={handleFocus}
      renderToken={
        (option, tokenProps, index) => (
          <Token
            key={index}
            onRemove={tokenProps.onRemove}
            option={option}
          >
            <TopicIcon className="mr-1" topic={option} />
            {topicName(option)}
          </Token>
        )
      }
      renderMenuItemChildren={
        (option, menuProps, _index) => [
          <TopicIcon className="mr-1" topic={option} key={option} />,
          <Highlighter key="name" search={menuProps.text}>
            {topicName(option)}
          </Highlighter>,
        ]
      }
    />
  )

  const topicName = (option) => option.translation?.name ?? option.name

  if (row) {
    return (
      <BsForm.Group controlId={field.name} as={Row}>
        <BsForm.Label column sm="3">{label}</BsForm.Label>
        <Col sm="9">
          {inputTypeahead}
        </Col>
        <BsForm.Control.Feedback type="invalid">
          {meta.error}
        </BsForm.Control.Feedback>
      </BsForm.Group>
    )
  }

  return (
    <BsForm.Group controlId={field.name}>
      <BsForm.Label>{label}</BsForm.Label>
      {inputTypeahead}
      <BsForm.Control.Feedback type="invalid">
        {meta.error}
      </BsForm.Control.Feedback>
    </BsForm.Group>
  )
}

TopicsInput.propTypes = {
  label: PropTypes.string,
  row: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
}

TopicsInput.defaultProps = {
  label: '',
  row: false,
  options: [],
}

export default withLocale('common', 'topicsInput')(TopicsInput)
